import { Injectable, DestroyRef, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Data, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { APP_CONFIG, AppConfig, AppSource } from '@application/config';

const DEFAULT_APP_TITLE = 'Actuar';

const APP_SOURCE_TITLES: Partial<Record<AppSource, string>> = {
  [AppSource.None]: DEFAULT_APP_TITLE,
  [AppSource.Actuar]: 'Actuar',
  [AppSource.Treino]: 'Treino',
  [AppSource.Afig]: 'AFIG',
  [AppSource.FacilFit]: 'FacilFit',
  [AppSource.PagueLa]: 'Pague.la',
  [AppSource.Accounts]: 'Accounts',
};

@Injectable({ providedIn: 'root' })
export class BreadcrumbTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appConfig = inject<AppConfig>(APP_CONFIG, { optional: true });
  private lastSnapshot: RouterStateSnapshot | null = null;

  constructor() {
    super();

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.lastSnapshot) {
          this.updateTitle(this.lastSnapshot);
        } else {
          this.titleService.setTitle(this.composeTitle());
        }
      });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.lastSnapshot = snapshot;

    const breadcrumbLabel = this.extractBreadcrumbLabel(snapshot.root);
    if (breadcrumbLabel) {
      const translatedLabel = this.translateService.instant(breadcrumbLabel);
      this.titleService.setTitle(this.composeTitle(translatedLabel || breadcrumbLabel));
      return;
    }

    const title = this.buildTitle(snapshot);
    if (title) {
      const translatedTitle = this.translateService.instant(title);
      this.titleService.setTitle(this.composeTitle(translatedTitle || title));
      return;
    }

    this.titleService.setTitle(this.composeTitle());
  }

  private composeTitle(sectionTitle?: string | null): string {
    const prefix = this.resolveAppTitle();
    if (sectionTitle && sectionTitle.trim().length > 0) {
      return `${prefix} | ${sectionTitle.trim()}`;
    }
    return prefix;
  }

  private resolveAppTitle(): string {

    if (typeof window !== 'undefined' && window['isFacilFit']) {
      return 'FácilFit';
    }

    if (!this.appConfig) {
      return DEFAULT_APP_TITLE;
    }

    return APP_SOURCE_TITLES[this.appConfig.appSource] ?? DEFAULT_APP_TITLE;
  }

  private extractBreadcrumbLabel(route: ActivatedRouteSnapshot | null): string | null {
    if (!route) {
      return null;
    }

    const isLastChild = route.firstChild === null;
    if (!isLastChild) {
      return this.extractBreadcrumbLabel(route.firstChild);
    }

    const titleData = this.getBreadcrumbData(route.routeConfig?.data ?? route.data);

    if (titleData && typeof titleData === 'string') {
      return titleData;
    }

    if (titleData && typeof titleData === 'object' && 'label' in titleData) {
      return titleData.label as string;
    }

    return null;
  }

  private getBreadcrumbData(data: Data | undefined): unknown {
    if (!data) {
      return null;
    }
    const breadcrumb = data['breadcrumb'];
    const title = data['title'];

    return title ?? breadcrumb;
  }
}
